import type { NextPage } from 'next'
import styles from '../styles/Applications.module.scss'
import { useState, useEffect, Key, useContext } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
	Row,
	Col,
	Button,
	Form
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDownAZ, faArrowUpAZ, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { createClient } from 'graphqurl'
import { useTranslation } from 'next-i18next'
import JobCard from '../components/Applications/JobCard'
import AllView from '../components/Applications/AllView'
import UserContext from '../UserContext'
import { useRouter } from 'next/router'

const Applications: NextPage = () => {
	const { user } = useContext(UserContext)
	const [page, setPage] = useState<number>(1)
	const [status, setStatus] = useState('all')
	const [sort, setSort] = useState('date')
	const [sortAsc, setSortAsc] = useState(false)
	const [total, setTotal] = useState(0)
	const [jobs, setJobs] = useState([])
	const { t } = useTranslation()
	const limit = 12
	const router = useRouter()
	const locale = router.locale?.toUpperCase()

	const getSort = (sort: string, asc: boolean) => {
		switch (sort) {
		case 'date':
			return `order_by: {createdAt: ${asc ? 'asc' : 'desc'}}`
		case 'company':
			return `order_by: {Job : {Company : {name: ${asc ? 'asc' : 'desc'}}}}`
		case 'jTitle':
			return `order_by: {Job : {title: ${asc ? 'asc' : 'desc'}}}`
		default:
			return `order_by: {createdAt: ${asc ? 'asc' : 'desc'}}`
		}
	}

	const getApplication = async () => {
		const token = window.localStorage.getItem('token')
		const client = createClient({
			endpoint: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
			headers: { authorization: token !== null ? `Bearer ${token}` : '' },
		})

		const hasuraQuery = await client.query({
			query:`
			{
				JobApplicant(${getSort(sort, sortAsc) + ', '}limit: ${limit}, offset: ${(page - 1) * limit},
				where: {
					Applicant: {
						id: {_eq: ${user.id}}
					},
					Job: {
						country: {_eq: "${locale}"}
					}
					${getStatus()}
				}) {
					createdAt
					Job {
						id
						title
						titleChinese
						titleMalay
						jobPostStart
						hideSalaries
						Company {
							name
							avatar
						}
						JobSalaries {
							min
							max
							jobType
							salaryType
						}
						Occupation {
							id
							name
						}
						JobLocations(order_by: {Branch: {id: asc}}) {
							Branch {
								Address {
									name
									postalCode
									streetAddress
									location
								}
							}
							MrtStation {
								id
								postalCode
								streetAddress
								name
								mrtLine
								MrtLine {
									colour
								}
							}
						}
						Category {
							name
						}
					}
				}
				JobApplicant_aggregate(
					where: {
						applicant: {_eq: ${user.id}}
						Job: {
							country: {_eq: "${locale}"}
						}
					}
				) {
					aggregate {
						count
					}
				}
			}
			`
		})

		const { data } = hasuraQuery
		const { JobApplicant: job, JobApplicant_aggregate: jobAggregate} = data

		setJobs(job)
		setTotal(jobAggregate.aggregate.count)
	}

	// localhost:3000/applications?page=3&status=selected&sort=date&sort=asc&limit=12
	// Fetch data from external API
	// const user = req.session.user
	// const token = user && user.token ? user.token : ''
	// const userId = user && user.id ? user.id : 0

	const getStatus = () => {
		switch (status) {
		case 'selected':
			return 'status: {_eq: "SUCCESSFUL"}'
		case 'not-selected':
			return 'status: {_eq: "UNSUCCESSFUL"}'
		case 'in-progress':
			return 'status: {_in: ["CONTACTUNLOCKED","PENDING","VIEWED","SHORTLISTED"]}'
		case 'all':
			return 'status: {_in: ["CONTACTUNLOCKED","PENDING","VIEWED","SHORTLISTED","SUCCESSFUL","UNSUCCESSFUL"]}'
		default:
			return 'status: {_in: ["CONTACTUNLOCKED","PENDING","VIEWED","SHORTLISTED","SUCCESSFUL","UNSUCCESSFUL"]}'
		}
	}


	const currentPage = page
	interface Pagination {
		prev: boolean,
		pagi: (number | string)[],
		next: boolean
	}
	const pagination: Pagination = {
		prev: false,
		pagi: [],
		next: false
	}
	// pagination.prev = false
	if (currentPage > 1) {
		pagination.prev = true
	}

	const numOfPage = Math.ceil(total / limit)
	pagination.pagi = []

	if (numOfPage < 6) {
		pagination.pagi = Array.from(Array(numOfPage), (e, i) => { return i + 1 })
	} else {
		if (currentPage < 3) {
			pagination.pagi = Array.from(Array(3), (e, i) => { return i + 1 })
			pagination.pagi.push('dotted')
			pagination.pagi.push(numOfPage)
		} else {
			const tempPagiItems: (number | string)[] = []

			if (currentPage - 1 > 1) {
				if (currentPage - 2 !== 1) {
					tempPagiItems.push(1)
					if (currentPage - 2 !== 1) {
						tempPagiItems.push('dotted')
					}
				}
			}

			if (currentPage - 1 > 1) {
				tempPagiItems.push(currentPage - 1)
			}

			tempPagiItems.push(currentPage)
			if (currentPage + 1 <= numOfPage) {
				tempPagiItems.push(currentPage + 1)
			}
			if (currentPage + 2 <= numOfPage) {
				// tempPagiItems.push(currentPage + 2)
				if (currentPage + 2 !== numOfPage) {
					if (currentPage + 3 < numOfPage) {
						tempPagiItems.push('dotted')
					}
					tempPagiItems.push(numOfPage)
				}
			}

			pagination.pagi = tempPagiItems
		}
	}

	if (currentPage < numOfPage) {
		pagination.next = true
	}

	let textMessage = ''
	if (status === 'in-progress' || status === 'all') {
		textMessage = 'applied.no_appl'
	} else if (status === 'selected') {
		textMessage = 'applied.no_succe'
	} else if (status === 'not-selected') {
		textMessage = 'applied.no_unsucce'
	}

	useEffect(() => {
		getApplication()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, sortAsc, status])

	return (
		<>
			<Row className='mt-2'>
				<Col>
					<h2>{t('header.applied')}</h2>
				</Col>
			</Row>
			<br />
			<Row>
				<Col className='pe-sm-0' xs={12} sm={9}>
					<Form.Select
						size="lg"
						aria-label="Job status select box"
						value={status}
						onChange={(event) => {
							setStatus(event.target.value)
						}}>
						<option value='all'>{t('applied.all_job_status')}</option>
						<option value='in-progress'>{t('applied.in_progress')}</option>
						<option value='selected'>{t('applied.selected')}</option>
						<option value='not-selected'>{t('applied.not_selected')}</option>
					</Form.Select>
				</Col>
				<Col>
					<Row className='mt-3 mt-sm-0'>
						<Col xs={7} sm={8} className='pe-sm-0'>
							<Form.Select
								size="lg"
								aria-label="filter options"
								value={sort}
								onChange={event => {
									setSort(event.target.value)
									setSortAsc(true)
								}}>
								<option value="date">{t('applied.date')}</option>
								<option value='company'>{t('applied.company_name')}</option>
								<option value="jTitle">{t('applied.job_title')}</option>
							</Form.Select>
						</Col>
						<Col xs={5} sm={4} className='pe-sm-0 text-center'>
							<Button variant='outline-*' size='lg' className={styles.filter_btn}
								onClick={() => {
									setSortAsc(!sortAsc)
								}}
							>
								{sortAsc ? <FontAwesomeIcon icon={faArrowDownAZ} /> : <FontAwesomeIcon icon={faArrowUpAZ} />}
							</Button>
						</Col>

					</Row>
				</Col>
			</Row>
			<Row>
				{(jobs?.length) ?
					(jobs.map((j: { Job: { id: number; Company: { avatar: string; name: string }; title: string; JobSalaries: any; hideSalaries: any }; createdAt: string }, i: Key | null | undefined) => {
						return (
							<JobCard
								key={i}
								id={j.Job.id}
								companyAvatar={j.Job.Company.avatar}
								companyName={j.Job.Company.name}
								jobTitle={j.Job.title}
								jobSalaries={j.Job.JobSalaries}
								hideSalaries={j.Job.hideSalaries}
								jobCreated={j.createdAt}
							/>
						)
					}))
					:
					<>
						<AllView
							textView={textMessage}
						/>
					</>
				}
			</Row>
			<br />
			{
				(jobs.length !== 0) &&
				<div className='justify-content-center text-center' style={{ marginTop: '0vh', backgroundColor: 'white', paddingTop: '2vh', paddingBottom: '18px' }}>
					<nav aria-label='Page navigation example'>
						<ul className='pagination justify-content-center pagination-md'>
							<li className={pagination.prev ? 'page-item' : 'page-item disabled'}>
								<Button className={`page-link ${styles.pagination}`}
									onClick={() =>
										setPage(page - 1)
									}
									aria-disabled='true'>
									<FontAwesomeIcon icon={faChevronLeft} width='12px' />
								</Button>
							</li>
							{pagination.pagi.map((pagi, index) => {
								return (
									<li key={index} className={pagi === 'dotted' ? 'page-item disabled' : (pagi === page ? 'page-item active' : 'page-item')}>
										<Button className={`page-link ${styles.pagination}`}
											onClick={() => setPage(pagi as number)}
										>
											{
												pagi === 'dotted' ? '...' : pagi
											}
										</Button>
									</li>
								)
							})}
							<li className={pagination.next ? 'page-item' : 'page-item disabled'}>
								<Button className={`page-link ${styles.pagination}`}
									onClick={() =>
										setPage(page + 1)
									}
								>
									<FontAwesomeIcon icon={faChevronRight} width='12px' />
								</Button>
							</li>
						</ul>
					</nav>
				</div>
			}
		</>
	)
}

export async function getStaticProps({ locale }) {
	return {
		props: {
			...(await serverSideTranslations(locale, ['common']))
			// Will be passed to the page component as props
		}
	}
}

export default Applications